import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Stethoscope } from 'lucide-react';
import type { PortalAvailabilityDay } from '@his/shared';
import { dateTime, timeOnly, todayIso } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, FormSkeleton, Select, Spinner, Textarea } from '@/components/ui/primitives';
import { addDaysIso, portalFetch, type PortalDoctor, type PortalPatient } from './PortalApp';

const RETURN_TO = '/portal';

export function PortalBookingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('portal');
  const me = useQuery({ queryKey: ['portal-me'], queryFn: () => portalFetch<PortalPatient>('/portal/me') });
  const byPhoneOnly = Boolean(me.data?.changesByPhoneOnly);

  const [doctorId, setDoctorId] = useState('');
  const [from, setFrom] = useState(todayIso());
  const [slotAt, setSlotAt] = useState('');
  const [reason, setReason] = useState('');

  const doctors = useQuery({
    queryKey: ['portal-doctors'],
    queryFn: () => portalFetch<PortalDoctor[]>('/portal/doctors'),
    staleTime: 60_000,
  });

  const availability = useQuery({
    queryKey: ['portal-availability', doctorId, from],
    queryFn: () => portalFetch<PortalAvailabilityDay[]>(`/portal/availability?doctorId=${doctorId}&from=${from}&days=7`),
    enabled: Boolean(doctorId),
    staleTime: 30_000,
  });

  const chosenDay = useMemo(
    () => availability.data?.find((day) => day.slots.some((slot) => slot.startsAt === slotAt)) ?? null,
    [availability.data, slotAt],
  );

  const book = useMutation({
    mutationFn: () =>
      portalFetch('/portal/appointments', {
        method: 'POST',
        body: { doctorId, slotAt, branchId: chosenDay?.branchId, reason: reason.trim() || undefined },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['portal-availability'] });
      navigate(RETURN_TO, { replace: true });
    },
  });

  const today = todayIso();
  const lastStart = addDaysIso(today, 7);
  const ready = Boolean(doctorId && slotAt);
  const dirty = !book.isSuccess && (doctorId !== '' || slotAt !== '' || reason !== '');

  if (me.isLoading) return <FormSkeleton />;

  return (
    <FormScreen
      title="Book an appointment"
      description="Pay at the reception when you arrive."
      icon={CalendarDays}
      eyebrow={t('screen.my_record')}
      backTo={RETURN_TO}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !byPhoneOnly) book.mutate();
      }}
    >
      {byPhoneOnly && <Alert>To book, change or cancel an appointment, please call the hospital.</Alert>}
      {book.error && <Alert variant="destructive">{(book.error as Error).message}</Alert>}

      <FormSection title={t('section.doctor')} icon={Stethoscope} columns={1}>
        {doctors.isLoading ? (
          <Spinner />
        ) : (
          <Field label="Doctor" required>
            <Select
              value={doctorId}
              disabled={byPhoneOnly}
              onChange={(event) => {
                setDoctorId(event.target.value);
                setSlotAt('');
              }}
            >
              <option value="">Choose a doctor</option>
              {doctors.data?.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                  {doctor.specialty ?? doctor.department ? ` — ${doctor.specialty ?? doctor.department}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {doctors.data?.length === 0 && <Alert>No clinics are open for online booking. Please call the hospital.</Alert>}
      </FormSection>

      {doctorId && (
        <FormSection title={t('section.time')} icon={CalendarDays} columns={1}>
          <div className="flex items-center justify-between gap-2">
            <Button type="button" size="sm" variant="outline" disabled={from <= today} onClick={() => setFrom(addDaysIso(from, -7))}>
              Earlier
            </Button>
            <span className="text-sm text-muted-foreground">Week from {from}</span>
            <Button type="button" size="sm" variant="outline" disabled={from >= lastStart} onClick={() => setFrom(addDaysIso(from, 7))}>
              Later
            </Button>
          </div>
          {availability.isLoading && <Spinner />}
          {availability.data?.length === 0 && <p className="text-sm text-muted-foreground">No online slots this week.</p>}
          {availability.data?.map((day) => (
            <div key={day.date}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day.date}</p>
              <div className="flex flex-wrap gap-1">
                {day.slots.map((slot) => (
                  <Button
                    key={slot.startsAt}
                    type="button"
                    size="sm"
                    variant={slotAt === slot.startsAt ? 'default' : 'outline'}
                    disabled={!slot.available}
                    onClick={() => setSlotAt(slot.startsAt)}
                  >
                    {timeOnly(slot.startsAt)}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </FormSection>
      )}

      <FormSection title={t('section.details')} columns={1}>
        <Field label="Reason for the visit">
          <Textarea rows={2} maxLength={400} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </FormSection>

      <FormActions
        submitLabel={`Book ${slotAt ? dateTime(slotAt) : ''}`.trim()}
        loading={book.isPending}
        disabled={byPhoneOnly}
        incomplete={!ready}
        cancelTo={RETURN_TO}
        note="Pay at the reception when you arrive."
        bleed={false}
      />
    </FormScreen>
  );
}
